import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { CAP_HERO } from "../lib/assets";

// Hero — editorial campaign spread for the flagship Cabo Unstoppable Cap
export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      data-testid="hero-section"
      className="relative w-full min-h-screen bg-[#050505] overflow-hidden flex flex-col"
    >
      {/* Coordinates rail — desktop only */}
      <div
        aria-hidden
        className="hidden lg:flex absolute left-7 top-0 bottom-0 z-20 flex-col items-center justify-center gap-6 pointer-events-none"
      >
        <div className="w-px h-16 bg-white/15" />
        <span className="font-mono text-[10px] tracking-[0.35em] text-white/35 [writing-mode:vertical-rl]">
          {t.hero.coords}
        </span>
        <div className="w-px h-16 bg-white/15" />
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 w-full max-w-[1920px] mx-auto">
        {/* Photography — full-bleed, first on mobile, right column on desktop */}
        <div className="order-1 lg:order-2 lg:col-span-7 relative h-[54vh] sm:h-[62vh] lg:h-auto lg:border-l lg:border-white/10">
          <Link
            to="/product/cabo-unstoppable-cap"
            data-testid="hero-product-image"
            className="group block absolute inset-0 overflow-hidden hero-img-reveal"
          >
            <img
              src={CAP_HERO}
              alt="The Original Cabo Unstoppable Cap — worn at Los Cabos beach with the Arch in the background"
              loading="eager"
              className="w-full h-full object-cover object-center image-cinematic transition-transform duration-[1400ms] ease-out group-hover:scale-[1.02]"
            />
            {/* Mobile: fade image into the content block below */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] to-transparent lg:hidden pointer-events-none" />

            {/* Editorial label — THE ORIGINAL */}
            <span className="absolute top-24 lg:top-32 left-5 lg:left-10 border-l-2 border-yellow-400 pl-3 text-[10px] font-bold tracking-[0.4em] uppercase text-white">
              {t.hero.originalBadge}
            </span>

            {/* Location stamp — desktop */}
            <span
              aria-hidden
              className="hidden lg:block absolute bottom-8 left-10 font-mono text-[10px] tracking-[0.35em] text-white/55"
            >
              {t.hero.location}
            </span>

            {/* View product — quiet text link over the image */}
            <span className="absolute bottom-6 right-5 lg:bottom-8 lg:right-10 inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-white/90 border-b border-white/40 pb-1 transition-colors group-hover:border-yellow-400 group-hover:text-white">
              {t.hero.viewProductOverlay} <ArrowRight className="w-3 h-3" />
            </span>
          </Link>
        </div>

        {/* Copy column */}
        <div className="order-2 lg:order-1 lg:col-span-5 relative z-10 flex flex-col justify-center px-6 sm:px-10 lg:pl-24 lg:pr-14 pt-8 pb-16 lg:py-28">
          {/* Overline row — drop label + coming soon status */}
          <div className="flex items-center justify-between gap-4 fade-up delay-100">
            <p
              data-testid="hero-overline"
              className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/50"
            >
              {t.hero.launchLabel}
            </p>
            <span
              data-testid="hero-coming-soon"
              className="inline-flex items-center gap-2.5"
            >
              <span className="w-1.5 h-1.5 bg-yellow-400 animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-yellow-400">
                {t.comingSoon.badge}
              </span>
            </span>
          </div>
          <div className="mt-4 h-px w-full bg-white/10 fade-up delay-100" />

          {/* Headline */}
          <h1
            data-testid="hero-title"
            className="h-display text-white mt-8 lg:mt-9 text-5xl sm:text-6xl md:text-7xl lg:text-[clamp(3.4rem,4.6vw,5.4rem)] fade-up delay-200"
          >
            <span className="block">{t.hero.capTitle1}</span>
            <span className="block lg:pl-[0.6em]">{t.hero.capTitle2}</span>
          </h1>

          <div className="mt-7 flex items-center gap-4 fade-up delay-300">
            <div className="w-10 h-px bg-yellow-400" />
            <p
              data-testid="hero-subtitle"
              className="text-[11px] tracking-[0.3em] uppercase font-semibold text-white/80"
            >
              {t.hero.capSubtitle}
            </p>
          </div>

          {/* Three-tier description */}
          <div className="mt-8 max-w-md fade-up delay-300">
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-yellow-400">
              {t.hero.capDescLead}
            </p>
            <p className="mt-4 text-white/65 text-base font-light leading-relaxed">
              {t.hero.capDescBody}
            </p>
            <p
              data-testid="hero-cap-closer"
              className="mt-4 font-editorial italic text-white text-lg leading-snug tracking-tight"
            >
              {t.hero.capDescCloser}
            </p>
          </div>

          {/* Stats block removed — editorial negative space between description and CTAs */}

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-9 fade-up delay-500">
            <Link
              to="/product/cabo-unstoppable-cap"
              data-testid="hero-shop-button"
              className="inline-flex items-center justify-center gap-3 bg-white text-black px-10 py-4 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-yellow-400 transition-colors"
            >
              {t.hero.shopNow} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#mindset"
              data-testid="hero-discover-button"
              className="cta-underline self-start sm:self-auto text-[11px] font-bold uppercase tracking-[0.25em] text-white/80 hover:text-white transition-colors"
            >
              {t.hero.discover}
            </a>
          </div>

          {/* Scroll cue */}
          <div className="mt-12 hidden lg:flex items-center gap-3 text-white/35 text-[10px] uppercase tracking-[0.35em] fade-up delay-500">
            <ArrowDown className="w-3.5 h-3.5" />
            {t.hero.scroll}
          </div>
        </div>
      </div>

      {/* Marquee ribbon */}
      <div className="shrink-0 relative border-t border-white/10 bg-[#050505] overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-3.5">
          {Array.from({ length: 2 }).flatMap((_, gi) =>
            [...t.hero.marquee, ...t.hero.marquee.slice(0, 3)].map((txt, i) => (
              <span
                key={`${gi}-${i}`}
                className="mx-9 font-mono text-[10px] tracking-[0.35em] text-white/50 inline-flex items-center gap-9"
              >
                {txt}
                <span className="w-1 h-1 bg-yellow-400" />
              </span>
            )),
          )}
        </div>
      </div>
    </section>
  );
};
